import React from 'react';
import { createPortal } from 'react-dom';
import { Globe } from 'lucide-react';
import { showToast } from '@/services/toastService';
import { getPortalUrl } from '@/utils/portalMap';

interface PortalCellProps {
  url?: string | null;
  label?: string | null;
}

// Small, accessible cell that shows a globe icon and opens/copies the portal link.
const PortalCell: React.FC<PortalCellProps> = ({ url, label }) => {
  const openLink = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!url) return showToast('Link não disponível', 'info');
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const resolvedUrl = url || getPortalUrl(label);

  const openResolved = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!resolvedUrl) return showToast('Link não disponível', 'info');
    window.open(resolvedUrl, '_blank', 'noopener,noreferrer');
  };

  const copyLink = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const text = resolvedUrl || label || '';
    if (!text) return showToast('Nada para copiar', 'info');
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // fallback
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      showToast('Link copiado', 'success');
    } catch (err) {
      showToast('Falha ao copiar', 'error');
    }
  };

  // lightweight hover/focus popup rendered in a portal to avoid clipping
  const [open, setOpen] = React.useState(false);
  const [anchorRect, setAnchorRect] = React.useState<{ top: number; left: number } | null>(null);
  const ref = React.useRef<HTMLDivElement | null>(null);

  return (
    <div
      className="relative flex items-center justify-center gap-2"
      onMouseEnter={(e) => {
        const el = ref.current;
        if (el) {
          const r = el.getBoundingClientRect();
          setAnchorRect({ top: r.bottom, left: r.left });
        }
        setOpen(true);
      }}
      onMouseLeave={() => { setOpen(false); setAnchorRect(null); }}
      onFocus={(e) => {
        const el = ref.current;
        if (el) {
          const r = el.getBoundingClientRect();
          setAnchorRect({ top: r.bottom, left: r.left });
        }
        setOpen(true);
      }}
      onBlur={() => { setOpen(false); setAnchorRect(null); }}
      ref={ref}
    >
      <button
        onClick={openResolved}
        title={resolvedUrl ? `Abrir ${resolvedUrl}` : label || 'Portal'}
        aria-label={resolvedUrl ? `Abrir portal ${resolvedUrl}` : label ? `Portal ${label}` : 'Portal'}
        className="p-1 rounded-md text-ink-subtitle dark:text-ink-subtitle-dark hover:bg-surface-alt dark:bg-surface-dark-alt focus:outline-none focus:ring-2 focus:ring-brand-500"
      >
        <Globe className="w-4 h-4" />
      </button>

      <button
        onClick={copyLink}
        title={resolvedUrl ? 'Copiar link do portal' : 'Copiar nome do portal'}
        aria-label={resolvedUrl ? 'Copiar link do portal' : 'Copiar nome do portal'}
        className="p-1 rounded-md text-ink-placeholder dark:text-ink-placeholder-dark hover:text-ink-subtitle dark:text-ink-subtitle-dark hover:bg-surface-alt dark:bg-surface-dark-alt focus:outline-none focus:ring-2 focus:ring-brand-500"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
        </svg>
      </button>

      {open && anchorRect && createPortal(
        <div style={{ position: 'fixed', top: anchorRect.top + 8, left: Math.max(anchorRect.left, 8), zIndex: 60 }} onClick={(e) => e.stopPropagation()}>
          <div className="w-64 bg-surface dark:bg-surface-dark border border-line dark:border-line-dark rounded-md shadow-lg p-2 text-sm">
            <div className="break-words text-xs text-ink-text dark:text-ink-text-dark">{resolvedUrl || label || 'Não disponível'}</div>
            <div className="flex justify-end gap-2 mt-2">
              <button onClick={(e) => { e.stopPropagation(); openResolved(e as any); }} className="px-2 py-1 text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">Abrir</button>
              <button onClick={copyLink} className="px-2 py-1 text-xs bg-surface-alt dark:bg-surface-dark-alt border rounded">Copiar</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default PortalCell;
